import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Calendar, FileText, Users, User,
  BookOpen, CalendarCog, CalendarDays, ChevronRight,
  Sparkles, ChevronLeft, PanelLeftClose, PanelLeftOpen, UserPlus, ChevronDown, Megaphone
} from 'lucide-react';

const Sidebar = ({ isOpen, onClose, isCollapsed, onToggleCollapse, role }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState({});

  const navigationItems = {
    student: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/student/dashboard', color: 'from-blue-500 to-cyan-500' },
      { icon: Calendar, label: 'Appointments', path: '/student/appointments', color: 'from-purple-500 to-pink-500' },
      {
        icon: FileText,
        label: 'Sessions',
        path: '/student/sessions',
      },
      { icon: User, label: 'Profile', path: '/student/profile', color: 'from-indigo-500 to-purple-500' },
    ],
    alumni: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/student/dashboard', color: 'from-blue-500 to-cyan-500' },
      { icon: Calendar, label: 'Appointments', path: '/student/appointments', color: 'from-purple-500 to-pink-500' },
      {
        icon: FileText,
        label: 'Sessions',
        path: '/student/sessions',
      },
      { icon: User, label: 'Profile', path: '/student/profile', color: 'from-indigo-500 to-purple-500' },
    ],
    staff: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/staff/dashboard', color: 'from-blue-500 to-cyan-500' },
      { icon: Calendar, label: 'Appointments', path: '/staff/appointments', color: 'from-purple-500 to-pink-500' },
      { icon: FileText, label: 'Sessions', path: '/staff/sessions', color: 'from-green-500 to-emerald-500' },
      { icon: User, label: 'Profile', path: '/staff/profile', color: 'from-indigo-500 to-purple-500' },
    ],
    faculty: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/staff/dashboard', color: 'from-blue-500 to-cyan-500' },
      { icon: Calendar, label: 'Appointments', path: '/staff/appointments', color: 'from-purple-500 to-pink-500' },
      { icon: FileText, label: 'Sessions', path: '/staff/sessions', color: 'from-green-500 to-emerald-500' },
      { icon: User, label: 'Profile', path: '/staff/profile', color: 'from-indigo-500 to-purple-500' },
    ],
    counselor: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/counselor/dashboard', color: 'from-blue-500 to-cyan-500' },
      { icon: Calendar, label: 'Appointments', path: '/counselor/appointments', color: 'from-purple-500 to-pink-500' },
      { icon: FileText, label: 'Sessions', path: '/counselor/sessions', color: 'from-green-500 to-emerald-500' },
      { icon: User, label: 'Profile', path: '/counselor/profile', color: 'from-indigo-500 to-purple-500' },
    ],
    admin: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard', color: 'from-blue-500 to-cyan-500' },
      { icon: Users, label: 'Users', path: '/admin/users/manage', color: 'from-violet-500 to-purple-500' },
      { icon: Calendar, label: 'Appointments', path: '/admin/appointments', color: 'from-purple-500 to-pink-500' },
      { icon: CalendarDays, label: 'Events', path: '/admin/calendar-events', color: 'from-amber-500 to-orange-500' },
      { icon: Megaphone, label: 'Announcements', path: '/admin/announcements', color: 'from-rose-500 to-pink-500' },
      { icon: User, label: 'Profile', path: '/admin/profile', color: 'from-indigo-500 to-purple-500' },
    ],
    receptionist: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/receptionist/dashboard', color: 'from-blue-500 to-cyan-500' },
      { icon: Users, label: 'Users', path: '/receptionist/users/manage', color: 'from-violet-500 to-purple-500' },
      { icon: Calendar, label: 'Appointments', path: '/receptionist/appointments', color: 'from-purple-500 to-pink-500' },
      { icon: CalendarDays, label: 'Events', path: '/receptionist/calendar-events', color: 'from-amber-500 to-orange-500' },
      { icon: Megaphone, label: 'Announcements', path: '/receptionist/announcements', color: 'from-rose-500 to-pink-500' },
      { icon: CalendarCog, label: 'Schedules', path: '/receptionist/schedules', color: 'from-teal-500 to-cyan-500' },
      { icon: User, label: 'Profile', path: '/receptionist/profile', color: 'from-indigo-500 to-purple-500' },
    ],
    hod: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/hod/dashboard', color: 'from-blue-500 to-cyan-500' },
      { icon: Calendar, label: 'Appointments', path: '/hod/appointments', color: 'from-purple-500 to-pink-500' },
      { icon: FileText, label: 'Sessions', path: '/hod/sessions', color: 'from-green-500 to-emerald-500' },
      { icon: Megaphone, label: 'Announcements', path: '/hod/announcements', color: 'from-rose-500 to-pink-500' },
      { icon: User, label: 'Profile', path: '/hod/profile', color: 'from-indigo-500 to-purple-500' },
    ],
  };

  const menuItems = navigationItems[role] || [];

  const handleNavigation = (path) => {
    navigate(path);
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  const toggleSubmenu = (label) => {
    setExpandedMenus(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  return (
    <>
      {/* Backdrop for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gradient-to-br from-black/60 via-black/50 to-black/40 backdrop-blur-md z-40 lg:hidden transition-all duration-300 animate-in fade-in"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 bottom-0 z-50
          w-20 md:${isCollapsed ? 'w-20' : 'w-64'}
          bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30
          dark:from-slate-900 dark:via-slate-800 dark:to-slate-900
          backdrop-blur-2xl border-r border-gray-200/80 dark:border-gray-800
          shadow-2xl shadow-black/10
          transform transition-all duration-300 ease-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 md:z-30 md:top-[70px]
          overflow-hidden
        `}
      >
        {/* Gradient overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 pointer-events-none" />

        <div className="relative h-full flex flex-col">
          {/* Mobile Header - Show only on screens smaller than md */}
          <div className="md:hidden flex flex-col items-center py-6 border-b border-gray-200/50 dark:border-gray-800 bg-white/30 dark:bg-slate-900/30 backdrop-blur-sm space-y-4">
            <div className="w-12 h-12 rounded-xl bg-white dark:bg-slate-800 flex items-center justify-center shadow-lg p-2 border border-blue-100 dark:border-blue-900/30">
              <img
                src="/usiu-logo-02.png"
                alt="USIU Logo"
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.src = "/usiu-logo.png";
                }}
              />
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-all duration-200"
              title="Close menu"
            >
              <ChevronLeft size={20} />
            </button>
          </div>

          {/* Branding and Collapse Toggle - Desktop Only (md and above) */}
          <div className="hidden md:flex items-center justify-between p-3 border-b border-gray-200/50 dark:border-gray-800">
            {!isCollapsed ? (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-2xl bg-white dark:bg-slate-800 flex items-center justify-center shadow-lg p-1">
                  <img
                    src="/usiu-logo-02.png"
                    alt="USIU Logo"
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.src = "/usiu-logo.png";
                    }}
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-bold bg-gradient-to-r from-usiu-blue-600 to-usiu-blue-500 bg-clip-text text-transparent leading-tight">
                    USIU
                  </span>
                  <span className="text-[0.65rem] font-semibold text-usiu-gold-500 leading-tight">
                    Counseling
                  </span>
                </div>
              </div>
            ) : (
              <div className="w-8 h-8 rounded-2xl bg-white dark:bg-slate-800 flex items-center justify-center shadow-lg mx-auto p-1">
                <img
                  src="/usiu-logo-02.png"
                  alt="USIU Logo"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.target.src = "/usiu-logo.png";
                  }}
                />
              </div>
            )}

            {!isCollapsed && (
              <button
                onClick={onToggleCollapse}
                className="p-2 rounded-2xl text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/50 dark:hover:bg-gray-800 transition-all duration-200"
                title="Collapse sidebar"
              >
                <PanelLeftClose size={18} />
              </button>
            )}
          </div>

          {/* Expand button when collapsed */}
          {isCollapsed && (
            <div className="hidden md:flex items-center justify-center p-2 border-b border-gray-200/50 dark:border-gray-800">
              <button
                onClick={onToggleCollapse}
                className="p-2 rounded-2xl text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/50 dark:hover:bg-gray-800 transition-all duration-200"
                title="Expand sidebar"
              >
                <PanelLeftOpen size={18} />
              </button>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-1 scrollbar-thin scrollbar-thumb-gray-300/50 scrollbar-track-transparent hover:scrollbar-thumb-gray-400/50">
            {menuItems.map((item, index) => {
              const isActive = location.pathname === item.path;
              const isSubmenuActive = item.submenu?.some(sub => location.pathname.startsWith(sub.path));
              const isExpanded = expandedMenus[item.label];
              const Icon = item.icon;
              const hasSubmenu = item.submenu && item.submenu.length > 0;

              return (
                <div key={item.path}>
                  <button
                    onClick={() => {
                      if (hasSubmenu && !isCollapsed) {
                        toggleSubmenu(item.label);
                      } else {
                        handleNavigation(item.path);
                      }
                    }}
                    className={`
                      group relative w-full flex items-center justify-center ${isCollapsed ? '' : 'md:justify-start md:px-3'} py-2.5 rounded-xl
                      font-medium text-sm transition-all duration-300 ease-out
                      ${isActive || isSubmenuActive
                        ? 'text-usiu-blue-600 dark:text-usiu-blue-400 bg-blue-50 dark:bg-blue-900/20'
                        : 'text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }
                    `}
                    style={{
                      animationDelay: `${index * 50}ms`
                    }}
                    title={isCollapsed ? item.label : ''}
                  >
                    {/* Hover effect */}
                    {!isActive && !isSubmenuActive && (
                      <div className="absolute inset-0 bg-gray-100 dark:bg-slate-800 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-300" />
                    )}

                    {/* Icon container */}
                    <div className={`
                      relative flex items-center justify-center ${isCollapsed ? 'w-8 h-8' : 'w-8 h-8 md:w-9 md:h-9'} rounded-lg
                      transition-all duration-300 ease-out
                      ${isActive || isSubmenuActive
                        ? 'bg-white/20 shadow-lg'
                        : 'bg-gray-100 dark:bg-slate-800 group-hover:bg-white dark:group-hover:bg-slate-700 group-hover:scale-110'
                      }
                    `}>
                      <Icon
                        className={`
                          w-4 h-4 md:${isCollapsed ? 'w-4 h-4' : 'w-[18px] h-[18px]'} transition-all duration-300
                          ${isActive || isSubmenuActive
                            ? 'text-usiu-blue-600 dark:text-usiu-blue-400'
                            : 'text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white group-hover:scale-110'
                          }
                        `}
                        strokeWidth={isActive || isSubmenuActive ? 2.5 : 2}
                      />
                    </div>

                    {/* Label */}
                    {!isCollapsed && (
                      <span className="relative flex-1 text-left font-semibold hidden md:block">
                        {item.label}
                      </span>
                    )}

                    {/* Chevron for submenu */}
                    {!isCollapsed && hasSubmenu && (
                      <ChevronDown
                        className={`relative w-4 h-4 transition-transform duration-300 hidden md:block ${isExpanded ? 'rotate-180' : ''
                          } ${isActive || isSubmenuActive ? 'text-white' : 'text-gray-600 dark:text-gray-400'}`}
                        strokeWidth={2}
                      />
                    )}

                    {/* Arrow indicator for active */}
                    {!isCollapsed && (isActive && !hasSubmenu) && (
                      <ChevronRight
                        className="relative w-4 h-4 text-white animate-in slide-in-from-left-2 duration-300 hidden md:block"
                        strokeWidth={3}
                      />
                    )}

                    {/* Shine effect on hover */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 overflow-hidden rounded-2xl">
                      <div className="absolute -inset-full top-0 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12 group-hover:animate-shine" />
                    </div>
                  </button>

                  {/* Submenu */}
                  {hasSubmenu && isExpanded && !isCollapsed && (
                    <div className="ml-6 mt-1 space-y-1">
                      {item.submenu.map((subItem) => {
                        const SubIcon = subItem.icon;
                        const isSubActive = location.pathname === subItem.path;

                        return (
                          <button
                            key={subItem.path}
                            onClick={() => handleNavigation(subItem.path)}
                            className={`
                              group relative w-full flex items-center gap-2 px-3 py-2 rounded-lg
                              font-medium text-sm transition-all duration-200
                              ${isSubActive
                                ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800'
                              }
                            `}
                          >
                            <SubIcon className="w-4 h-4" strokeWidth={isSubActive ? 2.5 : 2} />
                            <span className="flex-1 text-left hidden md:block">{subItem.label}</span>
                            {isSubActive && (
                              <div className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Footer decoration */}
          <div className="relative p-3 border-t border-gray-200/50 dark:border-gray-800">
            <div className={`flex items-center justify-center md:justify-start ${isCollapsed ? '' : 'md:gap-2'} text-xs text-gray-500 dark:text-gray-400`}>
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse shadow-lg shadow-green-500/50" />
              {!isCollapsed && <span className="hidden md:block">System Online</span>}
            </div>
          </div>
        </div>
      </aside>

      <style jsx>{`
        @keyframes shine {
          100% {
            transform: translateX(100%);
          }
        }
        .animate-shine {
          animation: shine 1.5s;
        }
      `}</style>
    </>
  );
};

export default Sidebar;